import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";
import BlockIcon from "@mui/icons-material/Block";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import {
  getInvoiceById,
  resetInvoiceServerStatus,
  voidInvoice,
  recordInvoicePayment,
} from "../redux/actions/invoiceActions";
import InvoiceDetailsTab from "../components/invoices/InvoiceDetailsTab";
import InvoicePdfTab from "../components/invoices/InvoicePdfTab";
import RecordPaymentModal from "../components/payments/RecordPaymentModal"; // Using unified modal
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppCircularProgress from "../components/common/AppCircularProgress";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { PaymentRequestDto } from "../shared/dtos/payment/PaymentRequestDto";

const InvoicePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    item: currentInvoice,
    loadingFetchOne: loadingInvoice,
    errorFetchOne: errorLoadingInvoice,
    serverStatus,
    loadingVoid: loadingVoidInvoice,
    loadingPayment: loadingPayInvoice,
    errorPayment: errorPayInvoice,
  } = useAppSelector((state) => state.invoice);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [isPayOpen, setIsPayOpen] = useState<boolean>(false);
  const [isVoidOpen, setIsVoidOpen] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);

    if (match && match[0]) {
      setInvoiceId(match[0]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (invoiceId) dispatch(getInvoiceById(invoiceId));
  }, [dispatch, invoiceId]);

  useEffect(() => {
    if (serverStatus?.status === 200 || serverStatus?.status === 201) {
      setIsPayOpen(false);
      setIsVoidOpen(false);
      dispatch(resetInvoiceServerStatus());
    }
  }, [serverStatus, dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void =>
    setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = (): void => setMenuAnchorEl(null);

  const handleRecordPayment = (
    id: string,
    payload: PaymentRequestDto,
  ): void => {
    dispatch(recordInvoicePayment(id, payload));
  };

  if (loadingInvoice) return <AppCircularProgress />;
  if (errorLoadingInvoice)
    return <Alert severity="error">{errorLoadingInvoice}</Alert>;
  if (!currentInvoice) return null;

  const balance = currentInvoice.total - currentInvoice.amountPaid;
  const isVoided = currentInvoice.status === "VOIDED";

  // --- RENDER FUNCTIONS FOR RESPONSIVE ACTIONS ---
  const renderDesktopActions = (): React.JSX.Element => (
    <Stack direction="row" spacing={1.5}>
      {balance > 0 && !isVoided && (
        <AppButton
          pill
          variant="contained"
          color="secondary"
          startIcon={<PaymentIcon />}
          onClick={() => setIsPayOpen(true)}
        >
          Record Payment
        </AppButton>
      )}

      {!isVoided && currentInvoice.status !== "PAID" && (
        <AppButton
          pill
          variant="outlined"
          color="error"
          startIcon={<BlockIcon />}
          onClick={() => setIsVoidOpen(true)}
        >
          Void
        </AppButton>
      )}
    </Stack>
  );

  const renderMobileActions = (): React.JSX.Element => (
    <Box>
      <IconButton onClick={handleMenuOpen} color="inherit">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: { sx: { borderRadius: 2, minWidth: 180 } },
        }}
      >
        {balance > 0 && !isVoided && (
          <MenuItem
            onClick={() => {
              setIsPayOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PaymentIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Record Payment" />
          </MenuItem>
        )}
        {!isVoided && currentInvoice.status !== "PAID" && (
          <MenuItem
            onClick={() => {
              setIsVoidOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Void Invoice" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Stack direction="row" sx={{ alignItems: "center" }} spacing={2}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/invoices")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Invoice #{currentInvoice.invoiceNumber}
            </Typography>
            <StatusChip status={currentInvoice.status} />
          </Stack>

          {isMobile ? renderMobileActions() : renderDesktopActions()}
        </Stack>

        <Typography
          variant="body2"
          sx={{ color: "text.secondary", ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Issued: {new Date(currentInvoice.issueDate).toLocaleDateString()} •
          Due: {new Date(currentInvoice.dueDate).toLocaleDateString()}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Invoice Details" />
          <Tab label="PDF Document" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <InvoiceDetailsTab invoice={currentInvoice} />}
        {activeTab === 1 && <InvoicePdfTab invoice={currentInvoice} />}
      </Box>

      {isPayOpen && (
        <RecordPaymentModal
          open={isPayOpen}
          onClose={() => setIsPayOpen(false)}
          entityId={currentInvoice.id}
          entityType="Invoice"
          documentNumber={currentInvoice.invoiceNumber}
          balance={balance}
          currencyCode={currentInvoice.currencyCode}
          customerId={currentInvoice.customerId}
          loading={loadingPayInvoice}
          error={errorPayInvoice}
          onSubmitPayment={handleRecordPayment}
        />
      )}

      <ConfirmDialog
        open={isVoidOpen}
        onClose={() => setIsVoidOpen(false)}
        onConfirm={() =>
          dispatch(voidInvoice(currentInvoice.id, "User requested void"))
        }
        title="Void Invoice"
        description="Are you sure you want to void this invoice? This cannot be undone."
        confirmText="Void"
        severity="error"
        loading={loadingVoidInvoice}
      />
    </Box>
  );
};
export default InvoicePage;
